import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, Trophy, ChevronRight, CirclePlus, RefreshCcw } from "lucide-react";

import { getCompetition } from "../../../api/competitions.api";
import { listMatches } from "../../../api/matches.api";
import Pagination from "../../../components/common/Pagination";

const STATE_TONE = {
  DRAFT:       "bg-bg-secondary text-text-secondary border-border-default",
  READY:       "bg-amber-500/15 text-amber-300 border-amber-400/30",
  IN_PROGRESS: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  COMPLETED:   "bg-blue-500/15 text-blue-300 border-blue-400/30",
};

export default function CompetitionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [comp, setComp] = useState(null);
  const [matches, setMatches] = useState(null);
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    getCompetition(id)
      .then((r) => { if (alive) setComp(r.data); })
      .catch(() => { if (alive) setError("Couldn't load this competition."); });
    return () => { alive = false; };
  }, [id]);

  useEffect(() => {
    let alive = true;
    listMatches(page, { competition: id, ordering: "-match_date" })
      .then((r) => { if (alive) setMatches(r.data); })
      .catch(() => { if (alive) setError("Couldn't load matches."); });
    return () => { alive = false; };
  }, [id, page]);

  const total = matches?.count ?? 0;
  const rows = matches?.results || [];

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md bg-bg-secondary border border-border-default text-text-primary hover:bg-bg-main transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={() => navigate("/cricket/competitions")}
          className="inline-flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary"
        >
          All competitions
        </button>
        <div className="flex-1" />
        <button
          onClick={() => navigate(`/cricket/competitions/${id}/sync`)}
          title="Map archival players to real profiles"
          className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md border border-button-primary/40 text-button-primary hover:bg-button-primary/10"
        >
          <RefreshCcw className="w-4 h-4" /> Sync players
        </button>
      </div>

      {comp ? (
        <div className="mb-5">
          <h2 className="text-xl font-bold">{comp.name}</h2>
          <p className="text-sm text-text-secondary mt-0.5 flex items-center gap-3 flex-wrap">
            {comp.season && (
              <span className="font-mono text-xs">{comp.season}</span>
            )}
            {comp.start_date && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" /> {comp.start_date}{comp.end_date ? ` → ${comp.end_date}` : ""}
              </span>
            )}
            {comp.tournament_name && (
              <span className="inline-flex items-center gap-1">
                <Trophy className="w-3.5 h-3.5" /> {comp.tournament_name}
              </span>
            )}
          </p>
        </div>
      ) : (
        <div className="text-text-secondary text-sm mb-5">Loading…</div>
      )}

      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm uppercase tracking-wider text-text-secondary">
          Matches {total > 0 && <span className="text-text-primary font-medium">· {total}</span>}
        </h3>
        <Link
          to="/cricket/matches/add"
          className="text-xs bg-button-primary hover:brightness-110 text-white px-3 py-1.5 rounded inline-flex items-center gap-1.5"
        >
          <CirclePlus className="w-3.5 h-3.5" /> Add match
        </Link>
      </div>

      {error && (
        <div className="rounded border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-200 mb-4">
          {error}
        </div>
      )}

      {matches && rows.length === 0 && (
        <div className="rounded-2xl border border-border-default bg-bg-primary p-8 text-center">
          <Trophy className="w-8 h-8 text-text-secondary mx-auto mb-3 opacity-60" />
          <h3 className="text-base font-semibold text-text-primary mb-1">
            No matches in this competition yet
          </h3>
          <p className="text-sm text-text-secondary mb-5 max-w-md mx-auto">
            Create a match and link it to this competition to see it here.
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="rounded-lg border border-border-default overflow-hidden">
          {rows.map((m) => (
            <button
              key={m.id}
              onClick={() => navigate(`/cricket/matches/${m.id}/scorecard`)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-bg-secondary/40 border-b border-border-default last:border-b-0 text-left"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-text-primary truncate">
                  {m.team1_name} <span className="text-text-secondary">vs</span> {m.team2_name}
                </div>
                <div className="text-[11px] text-text-secondary mt-0.5 flex items-center gap-2 flex-wrap">
                  {m.match_date && (
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {m.match_date}
                    </span>
                  )}
                  {m.match_type_name && (
                    <span className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-bg-secondary border border-border-default">
                      {m.match_type_name}
                    </span>
                  )}
                  {m.state && (
                    <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border ${STATE_TONE[m.state] || STATE_TONE.DRAFT}`}>
                      {m.state.replace("_", " ")}
                    </span>
                  )}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-text-secondary shrink-0" />
            </button>
          ))}
        </div>
      )}

      <Pagination count={total} page={page} onPage={setPage} />
    </div>
  );
}
